'use client';

import { format } from 'date-fns';
import * as React from 'react';
import { DateRange, SelectRangeEventHandler } from 'react-day-picker';

import { CalendarDots } from '@phosphor-icons/react';
import { cn } from '@utils/cn';
import { Button } from './button';
import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

export type DatePickerWithRangeProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'> & {
  date: DateRange | undefined;
  onSelect: (range: DateRange | undefined) => void;
  highlightTrigger?: boolean;
  invalid?: boolean;
};

export function DatePickerWithRange({
  className,
  date,
  highlightTrigger = false,
  invalid = false,
  ...props
}: DatePickerWithRangeProps) {
  const onSelect: SelectRangeEventHandler = (range) => {
    props?.onSelect(range);
  };

  return (
    <div className={cn('grid gap-2', className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={highlightTrigger ? 'default' : 'outline'}
            className={cn('justify-start text-left font-normal')}
            invalid={invalid}
          >
            <CalendarDots width="initial" height="initial" className="mr-2 h-4 w-4" />
            {date?.from ? (
              date.to ? (
                <>
                  {format(date.from, 'LLL dd, y')} - {format(date.to, 'LLL dd, y')}
                </>
              ) : (
                format(date.from, 'LLL dd, y')
              )
            ) : (
              <span>Pick a date</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 rounded-2xl shadow-2xl" align="start">
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={date?.from}
            selected={date}
            onSelect={onSelect}
            numberOfMonths={2}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
